import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { isDeepEqual, isPresent } from '@fcom/core/utils/utils';
import { Cabin } from '@fcom/dapi/api/models/cabin';
import { FinnairAirBoundsRequest } from '@fcom/dapi/api/models/finnair-air-bounds-request';
import { FinnairAirBoundsRequestItinerary } from '@fcom/dapi/api/models/finnair-air-bounds-request-itinerary';
import { LowestPriceOfPeriod } from '@fcom/dapi/api/models/lowest-price-of-period';
import { Price } from '@fcom/dapi/api/models/price';
import { GetPricesForPeriod$Params } from '@fcom/dapi/api/fn/instantsearch/get-prices-for-period';
import { GetPricesForPeriodWithFixedDepartureDate$Params } from '@fcom/dapi/api/fn/instantsearch/get-prices-for-period-with-fixed-departure-date';

import { INSTANT_SEARCH_CACHE_EXPIRY, INSTANT_SEARCH_FEATURE_KEY } from '../constants';
import { InstantSearchFeatureState, InstantSearchResult, InstantSearchState } from '../store.interface';

interface InstantSearchTripParams {
  departureLocationCode: string;
  destinationLocationCode: string;
  travelClass?: string;
  adults?: number;
  children?: number;
  infants?: number;
  locale?: string;
}

const travelClassMatches = (cabin: Cabin, travelClass: string | undefined): boolean =>
  cabin === Cabin.MIXED ? !travelClass || travelClass === Cabin.ECONOMY : travelClass === cabin;

const tripMatches = (
  params: InstantSearchTripParams,
  { locale, cabin, travelers }: FinnairAirBoundsRequest,
  outbound: FinnairAirBoundsRequestItinerary
): boolean =>
  params.departureLocationCode === outbound.departureLocationCode &&
  params.destinationLocationCode === outbound.destinationLocationCode &&
  params.locale === locale &&
  travelClassMatches(cabin, params.travelClass) &&
  (params.adults ?? 0) === (travelers.adults ?? 0) + (travelers.c15s ?? 0) &&
  (params.children ?? 0) === (travelers.children ?? 0) &&
  (params.infants ?? 0) === (travelers.infants ?? 0);

const isReturnOf = (outbound: FinnairAirBoundsRequestItinerary, inbound: FinnairAirBoundsRequestItinerary): boolean =>
  inbound.departureLocationCode === outbound.destinationLocationCode &&
  inbound.destinationLocationCode === outbound.departureLocationCode;

const findPrice = <P>(
  results: Array<InstantSearchResult<P>>,
  paramsMatch: (params: P) => boolean,
  date: string
): Price | undefined =>
  results
    .filter(({ params }) => paramsMatch(params))
    .map(({ response }) => response?.prices?.find((price) => price.date === date))
    .find(isPresent);

const cachedResponse = <P>(
  results: Array<InstantSearchResult<P>>,
  params: P,
  currentTime: number
): LowestPriceOfPeriod | undefined =>
  results.find(
    (result) => isDeepEqual(result.params, params) && result.createdAt + INSTANT_SEARCH_CACHE_EXPIRY > currentTime
  )?.response;

export const selectInstantSearchState = createFeatureSelector<InstantSearchFeatureState, InstantSearchState>(
  INSTANT_SEARCH_FEATURE_KEY
);

export const pricesForPeriodCacheSelector = (
  params: GetPricesForPeriod$Params,
  currentTime: number
): MemoizedSelector<InstantSearchFeatureState, LowestPriceOfPeriod | undefined> =>
  createSelector(selectInstantSearchState, ({ pricesForPeriod }) =>
    cachedResponse(pricesForPeriod, params, currentTime)
  );

export const pricesForPeriodPendingSelector = (
  params: GetPricesForPeriod$Params
): MemoizedSelector<InstantSearchFeatureState, boolean> =>
  createSelector(selectInstantSearchState, ({ pricesForPeriod }) =>
    pricesForPeriod.some((result) => result.pending && isDeepEqual(result.params, params))
  );

export const fixedDepartureCacheSelector = (
  params: GetPricesForPeriodWithFixedDepartureDate$Params,
  currentTime: number
): MemoizedSelector<InstantSearchFeatureState, LowestPriceOfPeriod | undefined> =>
  createSelector(selectInstantSearchState, ({ fixedDeparture }) => cachedResponse(fixedDeparture, params, currentTime));

export const instantSearchResultMatchSelector = (
  request: FinnairAirBoundsRequest
): MemoizedSelector<InstantSearchFeatureState, Price | undefined> =>
  createSelector(selectInstantSearchState, ({ pricesForPeriod, fixedDeparture }): Price | undefined => {
    const [outbound, inbound, ...rest] = request.itineraries;

    if (!inbound) {
      return findPrice(
        pricesForPeriod,
        (params) => params.oneway && tripMatches(params, request, outbound),
        outbound.departureDate
      );
    }

    if (rest.length > 0 || !isReturnOf(outbound, inbound)) {
      return undefined;
    }

    return findPrice(
      fixedDeparture,
      (params) => params.departureDate === outbound.departureDate && tripMatches(params, request, outbound),
      inbound.departureDate
    );
  });
